/**
 * ChallengeCard -- Reusable challenge card component with animated progress ring.
 *
 * Displays a challenge with: progress ring (SVG), days remaining countdown,
 * participant avatar stack, and join/view/completed button states.
 */

export interface ChallengeData {
  id: string;
  title: string;
  description: string;
  type: 'streak' | 'games' | 'reading' | 'memorization';
  target: number;
  progress: number;
  participantCount: number;
  startDate: string;
  endDate: string;
  joined: boolean;
  completed: boolean;
}

function getTypeColor(type: string): string {
  const colors: Record<string, string> = { streak: '#F59E0B', games: '#3B82F6', reading: '#14B8A6', memorization: '#8B5CF6' };
  return colors[type] || '#6366F1';
}

function getTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    streak: '<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>',
    games: '<rect x="2" y="6" width="20" height="12" rx="2"/>',
    reading: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
    memorization: '<rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 7h8M8 11h6M8 15h4"/>',
  };
  return `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${icons[type] || icons.games}</svg>`;
}

function daysRemaining(endDate: string): number {
  return Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000));
}

/**
 * Renders a progress ring SVG. The ring is a circle stroke that fills clockwise.
 */
function progressRingSvg(percent: number, color: string, size: number = 56): string {
  const r = (size - 6) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;

  return `
    <svg class="transform -rotate-90" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="var(--bg-secondary)" stroke-width="4"/>
      <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="${color}" stroke-width="4"
        stroke-linecap="round"
        stroke-dasharray="${circumference}"
        stroke-dashoffset="${offset}"
        style="transition: stroke-dashoffset var(--motion-duration-deliberate) var(--motion-easing-enter)"/>
    </svg>
  `;
}

function participantAvatars(count: number, color: string): string {
  const shown = Math.min(count, 4);
  const remaining = count - shown;
  const avatars = Array.from({ length: shown }, (_, i) => {
    const offset = i * 20;
    return `<div class="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold border-2 absolute" style="background: ${color}${20 + i * 10}; color: ${color}; border-color: var(--card-bg); ${`inset-inline-start: ${offset}px`}">
      <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="7" r="4"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></svg>
    </div>`;
  }).join('');

  return `
    <div class="relative h-7" style="width: ${shown * 20 + 8 + (remaining > 0 ? 28 : 0)}px">
      ${avatars}
      ${remaining > 0 ? `<div class="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold border-2 absolute" style="background: var(--bg-secondary); color: var(--text-secondary); border-color: var(--card-bg); inset-inline-start: ${shown * 20}px">+${remaining}</div>` : ''}
    </div>
  `;
}

export class ChallengeCard {
  render(
    container: HTMLElement,
    challenge: ChallengeData,
    t: (key: string, params?: Record<string, string | number>) => string,
    onTap: () => void,
  ): void {
    const color = getTypeColor(challenge.type);
    const pct = Math.min(100, Math.round((challenge.progress / challenge.target) * 100));
    const days = daysRemaining(challenge.endDate);

    let buttonHtml: string;
    if (challenge.completed) {
      buttonHtml = `<button class="challenge-btn px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1" style="background: #0A0A0A20; color: #0A0A0A">
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        ${t('community_completed')}
      </button>`;
    } else if (challenge.joined) {
      buttonHtml = `<button class="challenge-btn px-4 py-2 rounded-full text-xs font-bold" style="background: ${color}20; color: ${color}">${t('community_view')}</button>`;
    } else {
      buttonHtml = `<button class="challenge-btn px-4 py-2 rounded-full text-xs font-bold" style="background: ${color}; color: white">${t('community_join')}</button>`;
    }

    container.innerHTML = `
      <div class="challenge-card-inner flex items-center gap-3 p-4 rounded-2xl cursor-pointer transition-transform active:scale-[0.98]" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" role="button" tabindex="0">
        <!-- Progress Ring -->
        <div class="relative shrink-0 flex items-center justify-center">
          ${progressRingSvg(pct, color)}
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-xs font-bold" style="color: ${color}">${pct}%</span>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <span class="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold" style="background: ${color}15; color: ${color}">
              ${getTypeIcon(challenge.type)}
              ${t('community_challenge_type_' + challenge.type)}
            </span>
            ${!challenge.completed && days > 0 ? `<span class="text-[10px] font-semibold" style="color: var(--text-secondary)">${t('community_days_left', { count: days })}</span>` : ''}
          </div>
          <p class="text-sm font-semibold truncate mb-1" style="color: var(--text-primary)">${challenge.title}</p>
          <div class="flex items-center gap-3">
            ${participantAvatars(challenge.participantCount, color)}
            <span class="text-[10px]" style="color: var(--text-secondary)">${challenge.progress}/${challenge.target}</span>
          </div>
        </div>

        <!-- Button -->
        <div class="shrink-0">
          ${buttonHtml}
        </div>
      </div>
    `;

    const inner = container.querySelector('.challenge-card-inner');
    if (inner) {
      inner.addEventListener('click', onTap);
    }
  }
}
